// src/components/prescriptions/DocumentsManager.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import {
  listDocuments, createDocument, updateDocument, deleteDocument,
  getHistory, listPatients, listTemplates, getBranding,
  sendDocumentEmail, getProfessionals
} from '../../services/prescriptionsApi';
import PreviewPaper from './PreviewPaper';
import DocumentAIReviewCard from '../ai/DocumentAIReviewCard';
import { listDocumentReviews } from '../../services/contextualAiApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { confirmDiscardChanges, hasFormChanged } from '../../utils/unsavedChanges';

const getId = (obj) => obj?.id || obj?._id;

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function logPrescriptionAudit(action, form, details = {}) {
  if (!form?.patient_id) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  await axios.post(
    `${API_URL}/audit/log`,
    {
      patient_id: form.patient_id,
      action,
      details: {
        document_id: details.document_id || '',
        title: form.title || 'Documento odontológico',
        patient_name: form.patient_name || '',
        ...details,
      },
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

function buildPatientQualification(form) {
  const linhas = [];

  linhas.push(`PACIENTE: ${form.patient_name || ''}`);

  const docs = [];
  if (form.patient_cpf) docs.push(`CPF/MF de nº ${form.patient_cpf}`);
  if (form.patient_rg) docs.push(`Registro Geral ${form.patient_rg}`);

  if (docs.length) linhas.push(docs.join(', ') + '.');
  if (form.patient_address) linhas.push(`Endereço ${form.patient_address}.`);
  if (form.patient_phone) linhas.push(`Telefone ${form.patient_phone}.`);

  return linhas.filter(Boolean).join('\n');
}

function buildCertificateCidText(form) {
  const values = form.dynamic_fields_values || {};
  const cid = String(values.cid || '').trim();

  if (cid) {
    return (
      `Atesto, para fins ${values.finalidade || ''} e a pedido do(a) interessado(a), ` +
      `inclusive com menção de CID por este(a) solicitada, que o(a) paciente acima qualificado(a) ` +
      `esteve sob meus cuidados profissionais na presente data, ` +
      `no horário das ${values.hora_inicio || ''} às ${values.hora_fim || ''}, necessitando de ${values.repouso_horas || ''} ` +
      `de afastamento de suas atividades a partir desta data.\n\n` +
      `CID informado a pedido do(a) interessado(a): ${cid}`
    );
  }

  return (
    `Atesto, para fins ${values.finalidade || ''} e a pedido do(a) interessado(a), ` +
    `que o(a) paciente acima qualificado(a) esteve sob meus cuidados profissionais na presente data, ` +
    `no horário das ${values.hora_inicio || ''} às ${values.hora_fim || ''}, necessitando de ${values.repouso_horas || ''} ` +
    `de afastamento de suas atividades a partir desta data.`
  );
}

function getGuardianTypeLabel(type) {
  const map = {
    menor: 'responsável legal pelo(a) paciente menor',
    tutor: 'tutor(a)/guardião(ã) do(a) paciente',
    curador: 'curador(a) do(a) paciente',
    procurador: 'procurador(a)/representante do(a) paciente',
    incapaz: 'responsável legal pelo(a) paciente incapaz',
    acompanhante_autorizado: 'responsável/acompanhante autorizado(a)',
    outro: 'responsável/representante do(a) paciente',
  };

  return map[type] || 'responsável/representante do(a) paciente';
}

function buildLegalGuardianBlock(form) {
  if (!form.patient_is_minor && !form.legal_guardian_name) return '';

  const linhas = [];
  const tipo = getGuardianTypeLabel(form.legal_guardian_type);

  linhas.push(`RESPONSÁVEL / REPRESENTANTE: ${form.legal_guardian_name || ''}`);

  if (form.legal_guardian_cpf) {
    linhas.push(`CPF/MF de nº ${form.legal_guardian_cpf}.`);
  }

  if (form.legal_guardian_relationship) {
    linhas.push(`Vínculo: ${form.legal_guardian_relationship}.`);
  }

  if (form.legal_guardian_basis) {
    linhas.push(`Base da representação: ${form.legal_guardian_basis}.`);
  }

  linhas.push(`Qualificação: ${tipo}.`);

  return linhas.filter(Boolean).join('\n') + '\n';
}

function buildComparecimentoText(form) {
  const values = form.dynamic_fields_values || {};
  const acompanhanteNome = String(values.acompanhante_nome || '').trim();
  const acompanhanteCpf = String(values.acompanhante_cpf || '').trim();

  if (acompanhanteNome) {
    return `o(a) Sr(a). ${acompanhanteNome}${acompanhanteCpf ? `, RG/CPF/MF de nº ${acompanhanteCpf},` : ''} acompanhando o(a) paciente acima qualificado(a),`;
  }

  return `o(a) paciente acima qualificado(a)`;
}

function buildCertificateResponsibleText(form) {
  const values = form.dynamic_fields_values || {};
  const cid = String(values.cid_responsavel || '').trim();

  if (cid) {
    return (
      `Atesto, para fins ${values.finalidade || ''} e a pedido do(a) responsável/representante do(a) paciente, ` +
      `inclusive com menção de CID por este(a) solicitada, que o(a) paciente acima qualificado(a) ` +
      `esteve sob os meus cuidados profissionais em virtude de CID nº ${cid}, tendo sido submetido(a) ` +
      `a tratamento odontológico na presente data, das ${values.hora_inicio || ''} às ${values.hora_fim || ''}, ` +
      `sendo-lhe recomendado repouso por ${values.repouso_horas || ''}, além da necessidade de seguir as orientações ` +
      `e retornar conforme agendado.\n\n` +
      `CID informado a pedido do(a) responsável legal: ${cid}`
    );
  }

  return (
    `Atesto, para fins ${values.finalidade || ''} e a pedido do(a) responsável/representante do(a) paciente, ` +
    `que o(a) paciente acima qualificado(a) esteve sob os meus cuidados profissionais, tendo sido submetido(a) ` +
    `a tratamento odontológico na presente data, das ${values.hora_inicio || ''} às ${values.hora_fim || ''}, ` +
    `sendo-lhe recomendado repouso por ${values.repouso_horas || ''}, além da necessidade de seguir as orientações ` +
    `e retornar conforme agendado.`
  );
}

async function sha256HexFromBlob(blob) {
  const buffer = await blob.arrayBuffer();

  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    buffer
  );

  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function renderDocumentText(form) {
  let text = form.guidance_text || form.certificate_text || '';

  const values = form.dynamic_fields_values || {};

  text = text.replace(
    /\[\[IF:([\w.-]+)\]\]([\s\S]*?)\[\[\/IF\]\]/g,
    (_, key, content) => {
      const value = String(values[key] || '').trim();
      return value ? content : '';
    }
  );

  const issueDate = form.issue_date ? new Date(form.issue_date) : new Date();

  const dataExtenso = issueDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const parecerItems = [
    ['queixa_principal', 'Queixa principal / motivo da avaliação'],
    ['achados_clinicos', 'Achados clínicos relevantes'],
    ['exames_analisados', 'Exames analisados'],
    ['hipotese_clinica', 'Impressão diagnóstica / hipótese clínica'],
    ['conduta_sugerida', 'Conduta sugerida'],
  ];

  const blocoParecerOdontologico = parecerItems
    .filter(([key]) => String(values[key] || '').trim() !== '')
    .map(([key, label], index) => (
      `${index + 1}. ${label}:\n${values[key]}`
    ))
    .join('\n\n');  

  const replacements = {
    '{{PACIENTE_NOME_COMPLETO}}': form.patient_name || '',
    '{{PACIENTE_CPF}}': form.patient_cpf || '',
    '{{PACIENTE_RG}}': form.patient_rg || '',
    '{{PACIENTE_ENDERECO}}': form.patient_address || '',
    '{{PACIENTE_DATA_NASCIMENTO}}': form.patient_birth_date || '',
    '{{PACIENTE_TELEFONE}}': form.patient_phone || '',    
    '{{BLOCO_QUALIFICACAO_PACIENTE}}': buildPatientQualification(form),
    '{{TEXTO_ATESTADO_CID}}': buildCertificateCidText(form),
    '{{BLOCO_RESPONSAVEL_LEGAL}}': buildLegalGuardianBlock(form),
    '{{TEXTO_COMPARECIMENTO}}': buildComparecimentoText(form),
    '{{TEXTO_ATESTADO_RESPONSAVEL}}': buildCertificateResponsibleText(form),
    '{{DATA_EXTENSO}}': dataExtenso,
    '{{BLOCO_PARECER_ODONTOLOGICO}}': blocoParecerOdontologico,
  };

  Object.entries(replacements).forEach(([key, value]) => {
    text = text.replaceAll(key, value || '');
  });

  Object.entries(values).forEach(([key, value]) => {
    text = text.replaceAll(`{{${key}}}`, String(value || ''));
  });

  const textoLivre = String(form.free_text || '').trim();

  if (textoLivre) {
    const localEmissionPattern = /(\n*)([^.\n]*,\s*\d{2}\s+de\s+[a-zçãé]+\s+de\s+\d{4}\.)\s*$/i;

    if (localEmissionPattern.test(text)) {
      text = text.replace(localEmissionPattern, `\n\n${textoLivre}\n\n$2`);
    } else {
      text = `${text.trim()}\n\n${textoLivre}`;
    }
  }

  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function DocumentsManager({ patientId }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = (permission) => permissions.has(permission);
  const canUseAI = (user?.permissions || []).includes('ai.clinical_use');
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [docs, setDocs] = useState([]);
  const [history, setDocHistory] = useState([]);
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [branding, setBranding] = useState({});
  const [aiReviewsByDocument, setAiReviewsByDocument] = useState({});
  const [reviewingDocumentId, setReviewingDocumentId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const initialFormRef = useRef(null);
  const overlayMouseDownRef = useRef(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    template_id: '', patient_id: '', patient_name: '', patient_cpf: '', patient_address: '',
    medications: [], exam_requests: [], guidance_text: '', certificate_text: '',
    status: 'draft', title: '', issue_date: new Date().toISOString(),
    professional_id: '',
    dynamic_fields: [],
    dynamic_fields_values: {},
    free_text: '',
  });

  const [contactEmail, setContactEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [controlledCheck, setControlledCheck] = useState(null);
  const [checkingControlled, setCheckingControlled] = useState(false);
  const [generatingControlledPrefill, setGeneratingControlledPrefill] = useState(false);

  const [saveAndGenerateControlled, setSaveAndGenerateControlled] = useState(false);

  const isEditing = useMemo(() => Boolean(getId(selected)), [selected]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => getId(tpl) === form.template_id),
    [templates, form.template_id]
  );

  const isOdontoPrescriptionTemplate = useMemo(() => {
    return (
      selectedTemplate?.system_key === 'receita_odontologica_cfo_v1' ||
      selectedTemplate?.template_name === 'Receita Odontológica (CFO)'
    );
  }, [selectedTemplate]);

  const currentMedicationName = String(
    form.dynamic_fields_values?.medicamento || ''
  ).trim();

  const isPersistedControlledDocument =
    Boolean(form.is_controlled_document) ||
    form.document_mode === 'controlled_prefill' ||
    Boolean(form.controlled_medication_info) ||
    Boolean(form.controlled_prefill_pdf_url);

  const isDetectedControlledMedication =
    isOdontoPrescriptionTemplate &&
    controlledCheck?.is_controlled;

  const isControlledPrescriptionMode =
    isPersistedControlledDocument || isDetectedControlledMedication;

  const controlledInfo =
    controlledCheck?.is_controlled
      ? controlledCheck
      : form.controlled_medication_info || null;

  const controlledPrefillPdfUrl =
    form.controlled_prefill_pdf_url ||
    selected?.controlled_prefill_pdf_url ||
    '';

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Se vier patientId, filtra por paciente; senão, lista tudo como antes
      const docsParams = patientId ? { patient_id: patientId } : undefined;

      const [d, h, p, t, profs, aiReviews] = await Promise.all([
        listDocuments(docsParams),
        getHistory(50, 0, patientId),
        listPatients(100),
        listTemplates(),
        getProfessionals(),
        canUseAI && patientId ? listDocumentReviews(patientId).catch(() => []) : Promise.resolve([]),
      ]);
      setDocs(Array.isArray(d) ? d : []);
      setDocHistory(Array.isArray(h) ? h : []);
      setPatients(Array.isArray(p) ? p : []);
      setTemplates(Array.isArray(t) ? t : []);
      setProfessionals(Array.isArray(profs) ? profs : []);
      const latestByDocument = {};
      (aiReviews || []).forEach((item) => {
        const sourceId = String(item?.source_id || '');
        if (sourceId && !latestByDocument[sourceId]) latestByDocument[sourceId] = item;
      });
      setAiReviewsByDocument(latestByDocument);

      // Carrega branding inicial (padrão)
      const b = await getBranding(null);
      setBranding(b || {});
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  // Quando o patientId mudar (trocar de paciente no prontuário), recarrega a lista
  useEffect(() => {
    fetchAll();
  }, [patientId]);

  // Carrega branding quando muda o profissional selecionado
  useEffect(() => {
    if (!showModal) return; // só carrega se modal estiver aberto
    (async () => {
      try {
        const b = await getBranding(form.professional_id || null);
        setBranding(b || {});
      } catch (error) {
        console.error("Erro ao carregar branding:", error);
      }
    })();
  }, [form.professional_id, showModal]);

  const onOpenNew = () => {
    setSelected(null);
    setForm({
      template_id: '',
      template_key: '',
      template_name: '',
      patient_id: '',
      patient_name: '',
      patient_cpf: '',
      patient_address: '',
      medications: [],
      exam_requests: [],
      guidance_text: '',
      certificate_text: '',
      status: 'draft',
      title: '',
      issue_date: new Date().toISOString(),
      professional_id: '',
      dynamic_fields: [],
      dynamic_fields_values: {},
      free_text: '',

      is_controlled_document: false,
      document_mode: '',
      document_label: '',
      disable_regular_pdf: false,
      controlled_medication_info: null,
      controlled_prefill_pdf_url: '',
    });
    setContactEmail('');
    setControlledCheck(null);
    setShowModal(true);
  };

  const onOpenEdit = (item) => {
    setSelected(item);
    setForm({
      template_id: item.template_id || '',
      template_key: item.template_key || '',
      template_name: item.template_name || '',
      patient_id: item.patient_id || '',
      patient_name: item.patient_name || '',
      patient_cpf: item.patient_cpf || '',
      patient_address: item.patient_address || '',
      medications: item.medications || [],
      exam_requests: item.exam_requests || [],
      guidance_text: item.guidance_text || '',
      certificate_text: item.certificate_text || '',
      status: item.status || 'draft',
      title: item.title || '',
      issue_date: item.issue_date || new Date().toISOString(),
      professional_id: item.professional_id || '',
      dynamic_fields: item.dynamic_fields || [],
      dynamic_fields_values: item.dynamic_fields_values || {},
      free_text: item.free_text || '',

      // CAMPOS DO FLUXO CONTROLADO
      is_controlled_document: Boolean(item.is_controlled_document),
      document_mode: item.document_mode || '',
      document_label: item.document_label || '',
      disable_regular_pdf: Boolean(item.disable_regular_pdf),
      controlled_medication_info: item.controlled_medication_info || null,
      controlled_prefill_pdf_url: item.controlled_prefill_pdf_url || '',
    });
    setContactEmail('');
    setControlledCheck(item.controlled_medication_info || null);
    setShowModal(true);
  };

  const onClose = () => { setShowModal(false); setSelected(null); };
  const requestClose = () => {
    const isDirty = hasFormChanged(initialFormRef.current, form);
    if (confirmDiscardChanges(isDirty)) onClose();
  };

  useEffect(() => {
    if (showModal) initialFormRef.current = form;
  }, [showModal]);

  const handleOverlayMouseDown = (event) => {
    overlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayMouseUp = (event) => {
    if (overlayMouseDownRef.current && event.target === event.currentTarget) requestClose();
    overlayMouseDownRef.current = false;
  };

  const onChangePatient = (patientId) => {
    const p = patients.find((pp) => getId(pp) === patientId);
    setForm((f) => ({
      ...f,
      patient_id: patientId,
      patient_name: p?.full_name || '',
      patient_cpf: p?.cpf || '',
      patient_rg: p?.rg || '',
      patient_address: p?.address || '',
      patient_birth_date: p?.birth_date || '',
      patient_phone: p?.phone || '',
      patient_is_minor: Boolean(p?.is_minor_or_dependent),
      legal_guardian_name: p?.legal_guardian_name || '',
      legal_guardian_cpf: p?.legal_guardian_cpf || '',
      legal_guardian_relationship: p?.legal_guardian_relationship || '',
      legal_guardian_type: p?.legal_guardian_type || '',
      legal_guardian_basis: p?.legal_guardian_basis || '',
      legal_guardian_notes: p?.legal_guardian_notes || '',      
    }));
  };

  // aplica o template quando selecionado
  useEffect(() => {
    if (!form.template_id) return;
    const t = templates.find((tt) => getId(tt) === form.template_id);
    if (!t) return;
    setForm(f => ({
      ...f,
      template_key: t.system_key || '',
      template_name: t.template_name || '',
      title: t.title || t.template_name || '',
      medications: t.medications || [],
      exam_requests: t.exam_requests || [],
      guidance_text: '',
      certificate_text: t.certificate_text || t.guidance_text || f.certificate_text || '',
      dynamic_fields: t.dynamic_fields || [],
      dynamic_fields_values: f.dynamic_fields_values || {},
    }));
  }, [form.template_id, templates]);

  useEffect(() => {
    if (!isOdontoPrescriptionTemplate || !currentMedicationName) {
      setControlledCheck(null);
      return;
    }

    const timer = setTimeout(() => {
      checkControlledMedication();
    }, 700);

    return () => clearTimeout(timer);
  }, [isOdontoPrescriptionTemplate, currentMedicationName]);

  const checkControlledMedication = async () => {
    if (!isOdontoPrescriptionTemplate || !currentMedicationName) {
      setControlledCheck(null);
      return null;
    }

    setCheckingControlled(true);

    try {
      const token = localStorage.getItem('token');

      const { data } = await axios.post(
        `${API_URL}/prescriptions/check-controlled-medication`,
        { medication_name: currentMedicationName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setControlledCheck(data);
      return data;
    } catch (error) {
      console.error('Erro ao verificar medicamento controlado:', error);
      toast.error('Não foi possível verificar se o medicamento é controlado.');
      return null;
    } finally {
      setCheckingControlled(false);
    }
  };

  const onSave = async (e) => {
    e?.preventDefault?.();

    if (!form.patient_id || !form.patient_name) {
      toast.error('Selecione o paciente (nome obrigatório).');
      return;
    }

    setLoading(true);

    try {
      let saved;

      if (isEditing) {
        saved = await updateDocument(getId(selected), form);
        toast.success('Documento atualizado!');
      } else {
        saved = await createDocument(form);
        toast.success('Documento criado!');
      }

      console.log('Retorno create/update document:', saved);

      const savedDocumentId =
        saved?.id ||
        saved?._id ||
        saved?.document?.id ||
        saved?.document?._id ||
        saved?.data?.id ||
        saved?.data?._id ||
        saved?.data?.document?.id ||
        saved?.data?.document?._id ||
        getId(selected);

      if (savedDocumentId) {
        setReviewingDocumentId(String(savedDocumentId));
      }

      if (
        saveAndGenerateControlled &&
        controlledCheck?.is_controlled &&
        isOdontoPrescriptionTemplate
      ) {
        if (!savedDocumentId) {
          console.error('Não foi possível identificar o ID do documento salvo.', saved);
          toast.error('Documento salvo, mas não foi possível localizar o ID para gerar o pré-preenchimento.');
          await fetchAll();
          return;
        }

        await fetchAll();
        await generateControlledPrefill(savedDocumentId);
        onClose();
        return;
      }

      await fetchAll();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao salvar documento');
    } finally {
      setLoading(false);
      setSaveAndGenerateControlled(false);
    }
  };

  const generateControlledPrefill = async (forcedDocumentId = null) => {
    const documentId = forcedDocumentId || getId(selected);

    if (!documentId) {
      toast.error('Salve o documento antes de gerar o pré-preenchimento.');
      return;
    }

    setGeneratingControlledPrefill(true);

    try {
      const token = localStorage.getItem('token');

      const { data } = await axios.post(
        `${API_URL}/prescriptions/documents/${documentId}/generate-controlled-prefill`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(data.message || 'Pré-preenchimento gerado com sucesso.');

      setForm((prev) => ({
        ...prev,
        is_controlled_document: true,
        document_mode: 'controlled_prefill',
        document_label: 'Pré-preenchimento para receita controlada',
        disable_regular_pdf: true,
        title: 'PRÉ-PREENCHIMENTO PARA RECEITA CONTROLADA',
        controlled_medication_info: data.controlled_medication_info,
        controlled_prefill_pdf_url: data.pdf_url || data.controlled_prefill_pdf_url || '',
      }));

      setControlledCheck(data.controlled_medication_info);

      setSelected((prev) => (
        prev
          ? {
              ...prev,
              is_controlled_document: true,
              document_mode: 'controlled_prefill',
              document_label: 'Pré-preenchimento para receita controlada',
              disable_regular_pdf: true,
              title: 'PRÉ-PREENCHIMENTO PARA RECEITA CONTROLADA',
              controlled_medication_info: data.controlled_medication_info,
              controlled_prefill_pdf_url: data.pdf_url || data.controlled_prefill_pdf_url || '',
            }
          : prev
      ));

      if (!data.pdf_url) {
        toast.error('Pré-preenchimento gerado, mas o backend não retornou o link do PDF.');
      }

      await fetchAll();
    } catch (error) {
      console.error('Erro ao gerar pré-preenchimento:', error);
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao gerar pré-preenchimento para receita controlada.'
      );
    } finally {
      setGeneratingControlledPrefill(false);
      setSaveAndGenerateControlled(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir este documento?')) return;
    setLoading(true);
    try {
      await deleteDocument(id);
      showToast('Documento excluído', 'success');
      await fetchAll();
    }
    catch (e) {
      console.error(e);
      showToast(
        e?.response?.status === 403
          ? 'Você não tem permissão para excluir documentos.'
          : (e?.response?.data?.detail || 'Falha ao excluir documento'),
        'error'
      );
    }
    finally { setLoading(false); }
  };

  // ---------- PDF / Impressão / E-mail ----------
  const pageRef = useRef(null); // agora envolve TODAS as páginas (PreviewPaper usa ref no contêiner)

  async function buildPdf({ scale = 2, jpegQuality = 0.9 } = {}) {
    const node = pageRef.current;
    if (!node) throw new Error('Pré-visualização não encontrada');

    // Captura todas as páginas A4 independentes
    const pageNodes = Array.from(node.querySelectorAll('.a4-page'));
    if (pageNodes.length === 0) throw new Error('Nenhuma página A4 encontrada');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    for (let i = 0; i < pageNodes.length; i++) {
      const page = pageNodes[i];
      const canvas = await html2canvas(page, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL('image/jpeg', jpegQuality);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', x, y, w, h);
    }

    return pdf;
  }

  const onGeneratePDF = async () => {
    if (isControlledPrescriptionMode) {
      toast.error(
        'Este documento contém medicamento controlado. Gere apenas o pré-preenchimento para receita controlada.'
      );
      return;
    }

    try {

      const pdf = await buildPdf({
        scale: 1.8,
        jpegQuality: 0.9
      });

      const validationCode =
        `DOC-${crypto.randomUUID().slice(0,8).toUpperCase()}`;

      const validationUrl =
        `${window.location.origin}/public/validar-documento/${validationCode}`;

      const qrDataUrl = await QRCode.toDataURL(
        validationUrl,
        {
          width: 150,
          margin: 1
        }
      );

      const pageWidth =
        pdf.internal.pageSize.getWidth();

      const pageHeight =
        pdf.internal.pageSize.getHeight();

      pdf.setPage(
        pdf.getNumberOfPages()
      );

      pdf.addImage(
        qrDataUrl,
        'PNG',
        pageWidth - 75,
        pageHeight - 105,
        55,
        55
      );

      pdf.setFontSize(8);

      pdf.textWithLink(
        'Validar documento',
        pageWidth - 90,
        pageHeight - 43,
        { url: validationUrl }
      );

      pdf.text(
        validationCode,
        pageWidth - 82,
        pageHeight - 30
      );

      const finalBlob = pdf.output('blob');

      const sha256 = await sha256HexFromBlob(finalBlob);

      await axios.put(
        `${API_URL}/prescriptions/documents/${getId(selected)}/integrity`,
        {
          pdf_sha256: sha256,
          validation_code: validationCode
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      pdf.save(
        `${form.title || 'documento'}.pdf`
      );

      toast.success(
        'PDF gerado com autenticação.'
      );

    } catch (e) {
      console.error(e);
      toast.error(
        'Falha ao gerar PDF'
      );
    }
  };

  const onPrint = async () => {
    if (isControlledPrescriptionMode) {
      toast.error(
        'Impressão da receita comum bloqueada para medicamento controlado. Gere o pré-preenchimento.'
      );
      return;
    }

    try {
      const pdf = await buildPdf({ scale: 1.8, jpegQuality: 0.9 });
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const win = window.open(url);
      if (win) {
        const timer = setInterval(() => {
          if (win.document.readyState === 'complete') {
            win.focus(); win.print(); clearInterval(timer);
          }
        }, 300);
      }
      await logPrescriptionAudit('Documento/receituário preparado para impressão', form, {
        document_id: getId(selected),
        title: form.title || 'Documento odontológico',
        source: 'prescriptions_documents',
      });

      toast.success('Documento preparado para impressão!');
    } catch (e) {
      console.error(e); toast.error('Falha ao preparar impressão');
    }
  };

  const onSendEmail = async () => {
    if (isControlledPrescriptionMode) {
      toast.error(
        'Envio do PDF da receita comum bloqueado para medicamento controlado. Gere o pré-preenchimento.'
      );
      return;
    }

    if (!contactEmail) {
      toast.error('Informe o e-mail do destinatário');
      return;
    }
    setSendingEmail(true);
    try {
      toast.info('Enviando e-mail...', { autoClose: 1200 });

      // 1ª tentativa: qualidade boa
      let pdf = await buildPdf({ scale: 1.6, jpegQuality: 0.85 });
      let base64 = pdf.output('datauristring').split(',')[1];

      try {
        const resp = await sendDocumentEmail({
          to: contactEmail,
          subject: form.title || 'Documento odontológico',
          message: 'Segue documento em anexo.',
          filename: `${form.title || 'documento'}.pdf`,
          pdf_base64: base64,
        });
        console.log('sendDocumentEmail ->', resp);

        await logPrescriptionAudit('Documento/receituário enviado por e-mail', form, {
          document_id: getId(selected),
          to: contactEmail,
          filename: `${form.title || 'documento'}.pdf`,
          source: 'prescriptions_documents',
        });

        toast.success('E-mail enviado com sucesso!');
        return;
      } catch (e) {
        // Se for 413, tentamos reduzir tamanho automaticamente
        if (e?.response?.status === 413) {
          console.warn('413 detectado, reduzindo qualidade e refazendo PDF…');
          pdf = await buildPdf({ scale: 1.3, jpegQuality: 0.75 }); // menor
          base64 = pdf.output('datauristring').split(',')[1];

          const resp2 = await sendDocumentEmail({
            to: contactEmail,
            subject: form.title || 'Documento odontológico',
            message: 'Segue documento em anexo.',
            filename: `${form.title || 'documento'}.pdf`,
            pdf_base64: base64,
          });
          console.log('sendDocumentEmail (fallback)->', resp2);

          await logPrescriptionAudit('Documento/receituário enviado por e-mail', form, {
            document_id: getId(selected),
            to: contactEmail,
            filename: `${form.title || 'documento'}.pdf`,
            source: 'prescriptions_documents',
            fallback: 'reduced_pdf_quality',
          });

          toast.success('E-mail enviado com sucesso!');
          return;
        }
        throw e; // outro erro
      }
    } catch (e) {
      console.error('Falha ao enviar e-mail:', e);
      const msg = e?.response?.data?.detail || e?.message || 'Falha ao enviar e-mail';
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Documentos</h2>
        {can('documents.create') && <button data-onboarding-id="new-clinical-document" onClick={onOpenNew} className="px-3 py-2 bg-purple-600 text-white rounded-md">Novo documento</button>}
      </div>

      {loading && docs.length === 0 ? <div>Carregando...</div> : (
        <div className="space-y-2 mb-6">
          {canUseAI && patientId && reviewingDocumentId && docs.some((item) => String(getId(item)) === String(reviewingDocumentId)) && (
            <DocumentAIReviewCard
              patientId={patientId}
              documentId={reviewingDocumentId}
              existingAnalysis={aiReviewsByDocument[String(reviewingDocumentId)]}
              onAnalysisChange={(analysis) =>
                setAiReviewsByDocument((current) => ({
                  ...current,
                  [String(reviewingDocumentId)]: analysis,
                }))
              }
              onClose={() => setReviewingDocumentId(null)}
              onEditDocument={() => {
                const item = docs.find(
                  (document) => String(getId(document)) === String(reviewingDocumentId)
                );
                if (item) onOpenEdit(item);
              }}
            />
          )}
          {docs.length === 0 && <div className="text-sm text-gray-500">Nenhum documento encontrado.</div>}
          {docs.map((d) => (
            <div key={getId(d)} className="border rounded-md p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">
                  {d.document_label || d.title || 'Documento'}
                </div>
                <div className="text-xs text-gray-500">
                  {d.patient_name || d.patient_id} • {d.status || 'draft'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canUseAI && patientId && <button
                  type="button"
                  onClick={() => setReviewingDocumentId(String(getId(d)))}
                  className="px-3 py-1 border border-purple-200 text-purple-700 rounded-md hover:bg-purple-50"
                >
                  {aiReviewsByDocument[String(getId(d))]?.status === 'stale'
                    ? 'Conferir novamente'
                    : aiReviewsByDocument[String(getId(d))]?.status === 'accepted'
                    ? 'IA conferida'
                    : 'Conferir com IA'}
                </button>}
                <button onClick={() => onOpenEdit(d)} className="px-3 py-1 border rounded-md">{can('documents.update') ? 'Abrir / Editar' : 'Abrir'}</button>
                {can('documents.delete') && <button onClick={() => onDelete(getId(d))} className="px-3 py-1 border rounded-md">Excluir</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-2">
          {patientId ? 'Histórico recente deste paciente' : 'Histórico recente'}
        </h3>
        <div className="space-y-2">
          {history.length === 0 && <div className="text-sm text-gray-500">Sem histórico.</div>}
          {history.map((h) => (
            <div key={getId(h)} className="text-sm text-gray-600">
              {h.title || 'Documento'} — {h.status} — {new Date(h.updated_at || h.created_at).toLocaleString()}
            </div>
          ))}
        </div>
      </div>

      {/* Modal com rolagem e ações */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/30 p-4 sm:p-6 flex items-center justify-center z-50"
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayMouseUp}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-lg p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{isEditing ? 'Editar Documento' : 'Novo Documento'}</h3>
              <button type="button" onClick={requestClose} className="px-3 py-1 border rounded-md">Fechar</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <form onSubmit={onSave} className="space-y-3"> {/* ESQUERDA: FORMULÁRIO */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paciente</label>

                    {/* Busca de Paciente */}
                    <input
                      type="text"
                      placeholder="Buscar paciente..."
                      className="w-full border p-2 rounded mb-1 text-sm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <select
                      className="w-full border p-2 rounded"
                      value={form.patient_id}
                      onChange={(e) => onChangePatient(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {patients
                        .filter(p => p.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((p) => (
                          <option key={getId(p)} value={getId(p)}>{p.full_name}</option>
                        ))}
                    </select>
                  </div>
                  <label className="block">
                    <span className="text-sm text-gray-700">Profissional Responsável</span>
                    <select className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.professional_id || ''}
                      onChange={(e) => setForm({ ...form, professional_id: e.target.value })}>
                      <option value="">Padrão da Clínica</option>
                      {professionals.map((p) => (<option key={getId(p)} value={getId(p)}>{p.name}</option>))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm text-gray-700">Template (opcional)</span>
                    <select className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.template_id}
                      onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
                      <option value="">—</option>
                      {templates.map((t) => (<option key={getId(t)} value={getId(t)}>{t.template_name}</option>))}
                    </select>
                  </label>

                  <label className="block md:col-span-2">
                    <span className="text-sm text-gray-700">Título</span>
                    <input className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Ex.: Receita de antibiótico" />
                  </label>

                  <label className="block">
                    <span className="text-sm text-gray-700">CPF (opcional)</span>
                    <input className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.patient_cpf}
                      onChange={(e) => setForm({ ...form, patient_cpf: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-sm text-gray-700">Endereço (opcional)</span>
                    <input className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.patient_address}
                      onChange={(e) => setForm({ ...form, patient_address: e.target.value })} />
                  </label>

                  <label className="block">
                    <span className="text-sm text-gray-700">Data</span>
                    <input type="date" className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.issue_date?.slice(0, 10)}
                      onChange={(e) => setForm({ ...form, issue_date: new Date(e.target.value).toISOString() })} />
                  </label>
                </div>

                {Array.isArray(form.dynamic_fields) && form.dynamic_fields.length > 0 && (
                  <div className="border rounded p-3 bg-amber-50 space-y-3 md:col-span-2">
                    <h4 className="font-bold text-sm text-amber-800">
                      Campos específicos deste documento
                    </h4>

                    {form.dynamic_fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                        </label>

                        {field.help_text && (
                          <p className="text-xs text-gray-500 mb-1">
                            {field.help_text}
                          </p>
                        )}

                        <input
                          type="text"
                          className="w-full border p-2 rounded text-sm"
                          value={form.dynamic_fields_values?.[field.key] || ''}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              dynamic_fields_values: {
                                ...(f.dynamic_fields_values || {}),
                                [field.key]: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                {isOdontoPrescriptionTemplate && currentMedicationName && (
                  <div className="md:col-span-2">
                    {checkingControlled && (
                      <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                        Verificando se o medicamento é sujeito a controle especial...
                      </div>
                    )}

                    {isControlledPrescriptionMode && (
                      <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-3">
                        <div>
                          <h4 className="font-bold text-red-800 text-sm">
                            Atenção: medicamento sujeito a controle especial
                          </h4>
                          <p className="text-sm text-red-700 mt-1">
                            O medicamento informado foi identificado na base interna de medicamentos controlados.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="block text-xs text-red-700 font-semibold">
                              Princípio ativo
                            </span>
                            <span className="text-red-900">
                              {controlledInfo?.active_ingredient || currentMedicationName}
                            </span>
                          </div>

                          <div>
                            <span className="block text-xs text-red-700 font-semibold">
                              Grupo/lista
                            </span>
                            <span className="text-red-900">
                              {controlledInfo?.control_group || '-'}
                            </span>
                          </div>

                          <div>
                            <span className="block text-xs text-red-700 font-semibold">
                              Formulário necessário
                            </span>
                            <span className="text-red-900">
                              {controlledInfo?.required_form_label || 'Formulário físico apropriado'}
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-red-800">
                          {controlledInfo?.warning_text || 'Este medicamento exige formulário físico específico.'}
                        </p>

                        <div className="bg-white border border-red-100 rounded-md p-3 text-xs text-red-800">
                          O OdontoAssist AI não emite a receita controlada oficial. Esta função gera apenas um
                          <strong> pré-preenchimento para receita controlada</strong>, para auxiliar a transcrição
                          no formulário físico exigido.
                        </div>

                        <div className="bg-white border border-red-100 rounded-md p-3 text-xs text-red-800">
                          Para este tipo de medicamento, o fluxo correto é:
                          <ol className="list-decimal ml-4 mt-2 space-y-1">
                            <li>salvar o documento;</li>
                            <li>gerar o pré-preenchimento auxiliar;</li>
                            <li>transcrever no formulário físico oficial exigido.</li>
                          </ol>

                          {form.controlled_prefill_pdf_url && (
                            <div className="mt-3">
                {canUseAI && <button
                                type="button"
                                onClick={() => {
                                  const backendBase = process.env.REACT_APP_BACKEND_URL || '';
                                  window.open(
                                    `${backendBase}${form.controlled_prefill_pdf_url}`,
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                                }}
                                className="px-3 py-2 rounded-md border border-red-300 text-red-700 text-sm hover:bg-red-50"
                              >
                                Abrir pré-preenchimento já gerado
                </button>}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {controlledCheck && controlledCheck.is_controlled === false && (
                      <div className="border border-green-200 bg-green-50 rounded-lg p-3 text-sm text-green-800">
                        Medicamento não identificado como controlado na base interna atual.
                      </div>
                    )}
                  </div>
                )}

                <label className="block">
                  <span className="text-sm text-gray-700">Observações / texto livre complementar</span>
                  <textarea
                    className="mt-1 w-full border rounded-md px-3 py-2 min-h-[120px]"
                    value={form.free_text || ''}
                    onChange={(e) => setForm({ ...form, free_text: e.target.value })}
                    placeholder="Opcional: use este campo para acrescentar observações, recomendações ou um texto resumido complementar."
                  />
                </label>

                {/* Ações */}
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                  {!isControlledPrescriptionMode ? (
                    <>
                      {can('documents.create') && <button
                        type="button"
                        onClick={onGeneratePDF}
                        className="px-3 py-2 border rounded-md"
                      >
                        Gerar PDF
                      </button>}

                      <button
                        type="button"
                        onClick={onPrint}
                        className="px-3 py-2 border rounded-md"
                      >
                        Imprimir
                      </button>

                      {can('documents.send') && <div className="flex items-center gap-2 ml-auto">
                        <input
                          type="email"
                          placeholder="email@paciente.com"
                          className="border rounded px-2 py-1"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={onSendEmail}
                          disabled={sendingEmail}
                          className={`px-3 py-2 border rounded-md ${
                            sendingEmail ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                        >
                          {sendingEmail ? 'Enviando…' : 'Enviar e-mail'}
                        </button>
                      </div>}
                    </>
                  ) : (
                    <div className="w-full border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-800">
                      Esta prescrição foi identificada como <strong>medicamento sujeito a controle especial</strong>.
                      O OdontoAssist <strong>não irá gerar a receita odontológica comum em PDF</strong> para este caso.
                      Utilize apenas o <strong>pré-preenchimento para receita controlada</strong>, para transcrição no formulário físico exigido.
                    </div>
                  )}

                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={requestClose}
                      className="px-3 py-2 border rounded-md"
                    >
                      Cancelar
                    </button>

                    {isControlledPrescriptionMode ? (
                      <>
                        {(selected ? can('documents.update') : can('documents.create')) && <button
                          type="submit"
                          onClick={() => setSaveAndGenerateControlled(false)}
                          disabled={loading || generatingControlledPrefill}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md disabled:opacity-60"
                        >
                          {loading ? 'Salvando...' : 'Salvar'}
                        </button>}

                        {controlledPrefillPdfUrl ? (
                          <button
                            type="button"
                            onClick={() => {
                              const backendBase = process.env.REACT_APP_BACKEND_URL || '';
                              window.open(
                                `${backendBase}${controlledPrefillPdfUrl}`,
                                '_blank',
                                'noopener,noreferrer'
                              );
                            }}
                            className="px-4 py-2 bg-red-700 text-white rounded-md"
                          >
                            Abrir pré-preenchimento
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => generateControlledPrefill()}
                            disabled={generatingControlledPrefill || loading || !getId(selected)}
                            className="px-4 py-2 bg-red-700 text-white rounded-md disabled:opacity-60"
                          >
                            {generatingControlledPrefill
                              ? 'Gerando...'
                              : 'Gerar pré-preenchimento'}
                          </button>
                        )}
                      </>
                    ) : (
                      (selected ? can('documents.update') : can('documents.create')) && <button
                        type="submit"
                        onClick={() => setSaveAndGenerateControlled(false)}
                        disabled={loading || generatingControlledPrefill}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md disabled:opacity-60"
                      >
                        {loading ? 'Salvando...' : 'Salvar'}
                      </button>
                    )}
                  </div>
                </div>
              </form>

              <div className="border rounded-md overflow-auto max-h-[70vh]">
                <PreviewPaper
                  captureRef={pageRef}
                  branding={branding || {}}
                  content={{
                    title: form.title,
                    guidance_text: renderDocumentText(form),
                    patient_name: form.patient_name,
                    patient_cpf: form.patient_cpf,
                    patient_address: form.patient_address,
                    issue_date: form.issue_date,
                    doctor_name: branding?.doctor_name,
                  }}
                  variant="document"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
